import { useEffect, useMemo } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { useOpenWindow, useWindow } from '@krp/client-runtime'
import {
  AQSI_WINDOWS,
  aqsiMyShifts,
  aqsiReceiptRegisters,
  aqsiReceiptShifts,
  createShiftsViewActions,
  latestShiftArgsOf,
  myShiftsArgsOf,
  receiptsReloadRequested,
  registerShiftsArgsOf,
  registersInPlace,
  selectShiftsView,
  shiftChosen,
  shiftsRangeOf,
  shiftsVmOf,
  ShiftsScreen,
  type ShiftsActions,
  type ShiftsArgs,
} from '@krp/resource-aqsi-ui'
import { isoDayOf } from '@krp/ui-kit'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'

/** Элементы двух списков — одни и те же объекты кэша: перерисовка только при настоящей перемене. */
function sameItems<T>(left: readonly T[], right: readonly T[]): boolean {
  return left.length === right.length && left.every((item, index) => item === right[index])
}

/**
 * Binding окна «Смены» кабинета AQSI (раздел «Смены», решение владельца 26.10.2026). Rid — из контекста окна. Кассы места
 * читаются каждая своим запросом (`aqsiReceiptShifts`, тот же ключ кэша, что у дерева «Чеков»); когда номер последней смены
 * кассы известен, читается и её карточка — открыта ли касса сейчас. «Мои смены» за период — журнал работы службы (кабинет не
 * вызывается).
 */
export function AqsiShiftsBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  const resourceId = window.params.rid ?? ''
  const skip = resourceId === ''
  const today = isoDayOf(new Date())

  const view = useAppSelector((state: RootState) => selectShiftsView(state.aqsi.shifts, resourceId))
  const range = useMemo(() => shiftsRangeOf(view, today), [view, today])
  const registersArgs = useMemo(() => ({ resourceId }), [resourceId])
  const registers = aqsiReceiptRegisters.useQuery(registersArgs, { skip })
  const myArgs = useMemo(() => myShiftsArgsOf(resourceId, range), [resourceId, range])
  const myShifts = aqsiMyShifts.useQuery(myArgs ?? skipToken)

  const registerIds = useMemo(
    () => registersInPlace(registers.data, view.place).map((register) => register.id),
    [registers.data, view.place],
  )
  const listArgs = useMemo(() => registerIds.map((registerId) => registerShiftsArgsOf(resourceId, registerId)), [registerIds, resourceId])
  const lists = useAppSelector(
    (state: RootState) => listArgs.map((args) => aqsiReceiptShifts.select(args)(state)),
    sameItems,
  )
  // Номер последней смены — из прочитанного списка; ключ — строкой, чтобы подписка не пересоздавалась на каждую перерисовку.
  const latestKey = listArgs.map((args, index) => latestShiftArgsOf(resourceId, args.registerId, lists[index]?.data)?.detailsFor ?? '').join('|')
  const latestArgs = useMemo(
    () => listArgs.map((args, index): ShiftsArgs | null => {
      const detailsFor = latestKey.split('|')[index] ?? ''
      return detailsFor === '' ? null : { ...args, detailsFor }
    }),
    [listArgs, latestKey],
  )
  const latest = useAppSelector(
    (state: RootState) => latestArgs.map((args) => (args === null ? undefined : aqsiReceiptShifts.select(args)(state))),
    sameItems,
  )

  useEffect(() => {
    const subscriptions = [...listArgs, ...latestArgs.filter((args): args is ShiftsArgs => args !== null)].map((args) =>
      dispatch(aqsiReceiptShifts.initiate(args)),
    )
    return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
  }, [dispatch, listArgs, latestArgs])

  const vm = useMemo(
    () =>
      shiftsVmOf({
        resourceId,
        view,
        today,
        registers,
        shifts: listArgs.map((args, index) => {
          const detailed = latest[index]
          return { registerId: args.registerId, list: lists[index] ?? { isUninitialized: true, isLoading: false, isError: false, isSuccess: false }, ...(detailed !== undefined ? { latest: detailed } : {}) }
        }),
        myShifts,
      }),
    [resourceId, view, today, registers, listArgs, lists, latest, myShifts],
  )

  const actions = useMemo<ShiftsActions>(
    () => ({
      ...createShiftsViewActions(resourceId, dispatch, today),
      reload: () => dispatch(receiptsReloadRequested({ resourceId })),
      openReceipts: (registerId, number) => {
        dispatch(shiftChosen({ resourceId, registerId, shiftNumber: String(number) }))
        openWindow(AQSI_WINDOWS.receipts, { rid: resourceId })
      },
    }),
    [dispatch, openWindow, resourceId, today],
  )

  return <ShiftsScreen vm={vm} actions={actions} />
}
